import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EQUIPMENT_KINDS, type EquipmentKind, type HouseholdRole } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Section } from '../../components/Section';
import { SelectField } from '../../components/SelectField';
import { SettingsNav } from '../../components/SettingsNav';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { EQUIPMENT_LABEL, photoStorage, ROLE_LABEL, shortDate, timezones } from '../../lib/format';
import { createHouseholdFormSchema, parseForm, type FieldErrors } from '../../lib/forms';
import { FamilyMembersSection } from './FamilyMembersSection';

const ROLE_OPTIONS = (['owner', 'adult', 'member'] as const).map((r) => ({
  value: r,
  label: ROLE_LABEL[r],
}));

export function HouseholdPage() {
  const { auth, refresh } = useAuth();
  const qc = useQueryClient();
  const household = auth.status === 'authed' ? auth.household : null;
  const me = auth.status === 'authed' ? auth.user : null;
  const isOwner = auth.status === 'authed' && auth.role === 'owner';
  const canManage = isOwner || (auth.status === 'authed' && auth.role === 'adult');
  const [errors, setErrors] = useState<FieldErrors>({});

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.households.update>[0]) =>
      api.households.update(input),
    onSuccess: () => refresh(),
  });
  const members = useQuery({
    queryKey: queryKeys.members,
    queryFn: () => api.households.members.list(),
  });
  const invites = useQuery({
    queryKey: queryKeys.invites,
    queryFn: () => api.households.invites.list(),
    enabled: canManage,
  });
  const equipment = useQuery({
    queryKey: queryKeys.equipment,
    queryFn: () => api.households.equipment.list(),
  });
  const householdMe = useQuery({
    queryKey: queryKeys.householdMe,
    queryFn: () => api.households.me(),
  });

  const setRole = useMutation({
    mutationFn: (v: { userId: string; role: HouseholdRole }) =>
      api.households.members.updateRole(v.userId, { role: v.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.members }),
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api.households.members.remove(userId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.members });
      void qc.invalidateQueries({ queryKey: queryKeys.people });
    },
  });
  const leave = useMutation({
    mutationFn: () => api.households.members.remove(me!.id),
    onSuccess: () => refresh(),
  });
  const createInvite = useMutation({
    mutationFn: () => api.households.invites.create({}),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.invites }),
  });
  const revokeInvite = useMutation({
    mutationFn: (id: string) => api.households.invites.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.invites }),
  });
  const saveEquipment = useMutation({
    mutationFn: (list: Array<{ kind: EquipmentKind; count: number }>) =>
      api.households.equipment.replace({ equipment: list }),
    onSuccess: (data) => qc.setQueryData(queryKeys.equipment, data),
  });
  const [copied, setCopied] = useState<string | null>(null);

  function onSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = parseForm(createHouseholdFormSchema, e.currentTarget);
    if (r.errors) return setErrors(r.errors);
    setErrors({});
    update.mutate(r.data);
  }

  function onSaveEquipment(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const list = EQUIPMENT_KINDS.map((kind) => ({ kind, count: Number(fd.get(kind) ?? 0) || 0 }));
    saveEquipment.mutate(list);
  }

  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  }

  if (!household) return null;
  const counts = Object.fromEntries(
    (equipment.data?.equipment ?? []).map((e) => [e.kind, e.count]),
  );

  return (
    <AppShell title="Household">
      <SettingsNav />
      <div className="flex flex-col gap-4">
        {household.disabledAt ? (
          <Alert tone="error">
            This household was disabled on {shortDate(household.disabledAt)}.
          </Alert>
        ) : null}

        <Section title="Details">
          <form onSubmit={onSave} noValidate className="flex flex-col gap-3">
            <Field
              id="name"
              name="name"
              label="Household name"
              defaultValue={household.name}
              required
              error={errors['name']}
              disabled={!canManage}
            />
            <SelectField
              id="timezone"
              name="timezone"
              label="Time zone"
              defaultValue={household.timezone}
              options={timezones().map((z) => ({ value: z, label: z.replace(/_/g, ' ') }))}
              disabled={!canManage}
            />
            <SelectField
              id="unitSystem"
              name="unitSystem"
              label="Units"
              defaultValue={household.unitSystem}
              options={[
                { value: 'us', label: 'US customary' },
                { value: 'metric', label: 'Metric' },
              ]}
              disabled={!canManage}
            />
            <Field
              id="defaultServings"
              name="defaultServings"
              label="Default servings"
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              defaultValue={household.defaultServings}
              error={errors['defaultServings']}
              disabled={!canManage}
            />
            {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
            {update.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
            {canManage ? (
              <Button type="submit" disabled={update.isPending}>
                Save
              </Button>
            ) : (
              <p className="text-xs text-muted">Only owners and adults can change these.</p>
            )}
          </form>
        </Section>

        {householdMe.data?.mediaQuotaBytes !== undefined ? (
          <StorageSection
            used={householdMe.data.mediaBytesUsed ?? 0}
            quota={householdMe.data.mediaQuotaBytes}
          />
        ) : null}

        <Section title="Members">
          {members.isError ? <Alert tone="error">{messageForError(members.error)}</Alert> : null}
          {setRole.isError ? <Alert tone="error">{messageForError(setRole.error)}</Alert> : null}
          {removeMember.isError ? (
            <Alert tone="error">{messageForError(removeMember.error)}</Alert>
          ) : null}
          <ul className="flex flex-col divide-y divide-line">
            {members.data?.members.map((m) => {
              const self = m.user.id === me?.id;
              return (
                <li key={m.user.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {m.user.displayName}
                      {self ? <span className="text-muted"> (you)</span> : null}
                    </p>
                    <p className="truncate text-xs text-muted">
                      {m.user.email} · joined {shortDate(m.joinedAt)}
                    </p>
                  </div>
                  {isOwner && !self ? (
                    <div className="flex items-center gap-2">
                      <label className="sr-only" htmlFor={`role-${m.user.id}`}>
                        Role
                      </label>
                      <select
                        id={`role-${m.user.id}`}
                        value={m.role}
                        onChange={(e) =>
                          setRole.mutate({
                            userId: m.user.id,
                            role: e.target.value as HouseholdRole,
                          })
                        }
                        className="min-h-9 rounded-md border border-line bg-surface px-2 text-sm"
                      >
                        {ROLE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Remove ${m.user.displayName} from the household?`))
                            removeMember.mutate(m.user.id);
                        }}
                        className="min-h-9 rounded-lg border border-line px-2 text-sm text-fail"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs text-brand">
                      {ROLE_LABEL[m.role]}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Section>

        <FamilyMembersSection canManage={canManage} />

        {canManage ? (
          <Section title="Invites" description="Share a code; it works once and expires in 3 days.">
            {invites.isError ? <Alert tone="error">{messageForError(invites.error)}</Alert> : null}
            {createInvite.isError ? (
              <Alert tone="error">{messageForError(createInvite.error)}</Alert>
            ) : null}
            <ul className="flex flex-col gap-2">
              {invites.data?.invites
                .filter((i) => !i.usedAt)
                .map((i) => (
                  <li
                    key={i.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                  >
                    <code className="text-base font-semibold tracking-wider">{i.code}</code>
                    <span className="text-xs text-muted">expires {shortDate(i.expiresAt)}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void copy(i.code)}
                        className="min-h-9 rounded-lg border border-line px-2 text-sm"
                      >
                        {copied === i.code ? 'Copied' : 'Copy'}
                      </button>
                      <button
                        type="button"
                        onClick={() => revokeInvite.mutate(i.id)}
                        className="min-h-9 rounded-lg border border-line px-2 text-sm text-fail"
                      >
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
            </ul>
            <Button
              variant="secondary"
              onClick={() => createInvite.mutate()}
              disabled={createInvite.isPending}
            >
              New invite code
            </Button>
          </Section>
        ) : null}

        <Section
          title="Kitchen equipment"
          description="Used to schedule several recipes at once without double-booking the oven or burners."
        >
          <form onSubmit={onSaveEquipment} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {EQUIPMENT_KINDS.map((kind) => (
                <Field
                  key={kind}
                  id={`eq-${kind}`}
                  name={kind}
                  label={EQUIPMENT_LABEL[kind] ?? kind}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={20}
                  defaultValue={counts[kind] ?? 0}
                  disabled={!canManage}
                />
              ))}
            </div>
            {saveEquipment.isError ? (
              <Alert tone="error">{messageForError(saveEquipment.error)}</Alert>
            ) : null}
            {saveEquipment.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
            {canManage ? (
              <Button type="submit" disabled={saveEquipment.isPending}>
                Save equipment
              </Button>
            ) : null}
          </form>
        </Section>

        <Section title="Leave household">
          {leave.isError ? <Alert tone="error">{messageForError(leave.error)}</Alert> : null}
          <Button
            variant="danger"
            onClick={() => {
              if (confirm(`Leave ${household.name}?`)) leave.mutate();
            }}
            disabled={leave.isPending}
          >
            Leave {household.name}
          </Button>
        </Section>
      </div>
    </AppShell>
  );
}

function StorageSection({ used, quota }: { used: number; quota: number }) {
  const s = photoStorage(used, quota);
  return (
    <Section title="Storage">
      <p className="text-sm">{s.line}</p>
      <div
        role="progressbar"
        aria-label="Photo storage used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(s.fraction * 100)}
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-line"
      >
        <div
          className={`h-full rounded-full ${s.fraction >= 0.9 ? 'bg-warn' : 'bg-brand'}`}
          style={{ width: `${Math.max(s.fraction * 100, used > 0 ? 1 : 0)}%` }}
        />
      </div>
      {s.full ? (
        <p className="mt-2 text-xs text-muted">Storage is full. Remove some photos to add more.</p>
      ) : null}
    </Section>
  );
}
